import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "../../../Styles/components/pagination.scss";

function buildPageNumbers(totalPages, currentPage, maxVisible = 5) {
  if (!totalPages || totalPages <= 0) return [];

  const safeVisible = Math.min(maxVisible, totalPages);
  let start = 1;
  let end = safeVisible;

  if (currentPage > Math.floor(safeVisible / 2) + 1) {
    start = Math.max(1, currentPage - Math.floor(safeVisible / 2));
    end = Math.min(totalPages, start + safeVisible - 1);

    if (end - start + 1 < safeVisible) {
      start = Math.max(1, end - safeVisible + 1);
    }
  }

  if (currentPage >= totalPages - Math.floor(safeVisible / 2)) {
    start = Math.max(1, totalPages - safeVisible + 1);
    end = totalPages;
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function Pagination({
  currentPage = 1,
  totalPages = 0,
  totalCount = 0,
  pageSize = 0,
  onPageChange,
  showFirstLast = true,
  className = "",
}) {
  const safeCurrentPage = Math.min(Math.max(currentPage || 1, 1), totalPages || 1);
  const pageNumbers = useMemo(
    () => buildPageNumbers(totalPages, safeCurrentPage),
    [safeCurrentPage, totalPages],
  );

  const rangeLabel = useMemo(() => {
    if (!totalCount || !pageSize) return null;

    const start = (safeCurrentPage - 1) * pageSize + 1;
    const end = Math.min(totalCount, start + pageSize - 1);
    return `${start}-${end} of ${totalCount}`;
  }, [pageSize, safeCurrentPage, totalCount]);

  if (!totalPages) {
    return null;
  }

  return (
    <div className={["app-pagination", className].filter(Boolean).join(" ")}>
      <div className="app-pagination__summary">
        <span>
          Page {safeCurrentPage} of {totalPages}
        </span>
        {rangeLabel ? (
          <small>{rangeLabel}</small>
        ) : totalCount ? (
          <small>{totalCount} total records</small>
        ) : null}
      </div>

      <div className="app-pagination__controls">
        {showFirstLast ? (
          <button
            type="button"
            disabled={safeCurrentPage === 1}
            onClick={() => onPageChange?.(1)}
            aria-label="First page"
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
        ) : null}

        <button
          type="button"
          disabled={safeCurrentPage === 1}
          onClick={() => onPageChange?.(safeCurrentPage - 1)}
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        {pageNumbers.map((page) => (
          <button
            key={page}
            type="button"
            className={page === safeCurrentPage ? "is-active" : ""}
            onClick={() => onPageChange?.(page)}
          >
            {page < 10 ? `0${page}` : page}
          </button>
        ))}

        <button
          type="button"
          disabled={safeCurrentPage === totalPages}
          onClick={() => onPageChange?.(safeCurrentPage + 1)}
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>

        {showFirstLast ? (
          <button
            type="button"
            disabled={safeCurrentPage === totalPages}
            onClick={() => onPageChange?.(totalPages)}
            aria-label="Last page"
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
